import { MyFavoritesItemStatusTab } from '@/pages/MyItems/MyFavorites/types/MyFavoritesPage.types';
import * as similarItemsGroupsSearchApi from '@/redux/api/similarItemsGroups';
import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getIsBot } from '@/redux/modules/analytics';
import { getClientIpAddress } from '@/redux/modules/browser';
import { getDeployment } from '@/redux/modules/config';
import { fetchSavedItems } from '@/redux/modules/item/saved/savedItems.actions';
import { fetchRecentItemsIfNeeded } from '@/redux/modules/recentItem';
import { SimilarItemsGroupsApiRow } from '@/redux/modules/similarItemsGroups/similarItemsGroups.types';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';

import {
    getRecommendedSimilarItemsSavedSeedItemIds,
    getRecommendedSimilarItemsSeedItemIds,
    shouldFetchRecommendedSimilarItems,
} from './recommendedSimilarItems.selectors';
import {
    FetchRecommendedSimilarItemsPayload,
    FetchRecommendedSimilarItemsRequest,
    RECOMMENDED_SIMILAR_ITEMS_LIMIT,
    RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT,
} from './recommendedSimilarItems.types';

const SAVED_ITEMS_PAGE_SIZE = 24;

export const fetchRecommendedSimilarItems = createAsyncThunk<
    FetchRecommendedSimilarItemsPayload,
    FetchRecommendedSimilarItemsRequest
>(
    'la/ui/recommendedSimilarItems/fetchRecommendedSimilarItems',
    async ({ shouldUseLastTwoSavedLots, shouldUseLastTwoViewedLots, skipSavedItemsFetch }, { dispatch, getState }) => {
        if (shouldUseLastTwoSavedLots && !skipSavedItemsFetch) {
            await dispatch(
                fetchSavedItems({
                    page: 1,
                    pageSize: SAVED_ITEMS_PAGE_SIZE,
                    sort: SearchSortAndDirection.SavedMostRecent,
                    status: MyFavoritesItemStatusTab.Available,
                })
            );
        }

        const currentState = getState();
        const savedSeedItemIds = shouldUseLastTwoSavedLots
            ? getRecommendedSimilarItemsSavedSeedItemIds(currentState)
            : [];
        const shouldUseViewedSeedItemIds =
            shouldUseLastTwoViewedLots ||
            (shouldUseLastTwoSavedLots && savedSeedItemIds.length < RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT);

        if (shouldUseViewedSeedItemIds) {
            await dispatch(fetchRecentItemsIfNeeded());
        }

        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);
        const seedItemIds = getRecommendedSimilarItemsSeedItemIds(state, {
            shouldUseLastTwoSavedLots,
            shouldUseLastTwoViewedLots,
        });

        const responses = await Promise.all(
            seedItemIds.map((itemId) =>
                similarItemsGroupsSearchApi.fetchSimilarItemsGroups({
                    authToken,
                    deployment,
                    ipAddress,
                    isBot,
                    itemId,
                    useNewSimilarItems: true,
                    useScoutCategories: true,
                    useSignalsBoost: true,
                })
            )
        );

        const itemIds = [
            ...new Set(
                responses.flatMap(({ payload }) =>
                    (payload as SimilarItemsGroupsApiRow[]).flatMap((row) => row.items.map((item) => item.itemId))
                )
            ),
        ]
            .filter((itemId) => !seedItemIds.includes(itemId))
            .slice(0, RECOMMENDED_SIMILAR_ITEMS_LIMIT);

        return {
            actionTime: Date.now(),
            itemIds,
            seedItemIds,
        };
    },
    {
        condition: ({ forceUpdate, shouldUseLastTwoSavedLots, shouldUseLastTwoViewedLots }, { getState }) =>
            forceUpdate ||
            shouldFetchRecommendedSimilarItems(getState(), {
                shouldUseLastTwoSavedLots,
                shouldUseLastTwoViewedLots,
            }),
    }
);
